import './index.css';
import { isPrerenderMode } from './lib/isPrerenderMode';

// All homepage styles are already inline. Give Vite an inert reference so lazy
// imports do not download them again; a parser-created link would still preload.
for (const inlineStyles of document.querySelectorAll<HTMLStyleElement>('style[data-home-styles]')) {
  if (!inlineStyles.dataset.homeStyles) continue;
  const marker = document.createElement('link');
  marker.disabled = true;
  marker.rel = 'stylesheet';
  marker.href = inlineStyles.dataset.homeStyles;
  document.head.appendChild(marker);
}

const start = () => import('./hydrate');
const isHome = location.pathname.replace(/\/+$/, '') === import.meta.env.BASE_URL.replace(/\/+$/, '');
const homeShell = document.getElementById('bukietowa-home-shell');
if ((!isHome || isPrerenderMode) && homeShell) {
  // Generic static preview hosts can fall back to index.html for every path.
  // Never hydrate its homepage markup as another route or as a full snapshot.
  document.getElementById('root')?.replaceChildren();
  homeShell.remove();
}
if (isHome && homeShell && !isPrerenderMode) {
  // The public HTML is usable already. Start React after its first paint so
  // hydration cannot delay that paint; an interaction always starts it sooner.
  let started = false;
  let queuedClick: HTMLButtonElement | null = null;
  let observer: PerformanceObserver | undefined;
  const hydrate = () => {
    if (started) return;
    started = true;
    observer?.disconnect();
    window.removeEventListener('pointerdown', hydrate);
    window.removeEventListener('keydown', hydrate);
    void start();
  };
  window.addEventListener('pointerdown', hydrate, { once: true, passive: true });
  window.addEventListener('keydown', hydrate, { once: true });
  const rememberClick = (event: MouseEvent) => {
    const button = (event.target as Element)?.closest<HTMLButtonElement>('button');
    if (!button || button.disabled) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    queuedClick = button;
    hydrate();
  };
  document.addEventListener('click', rememberClick, true);
  window.addEventListener('bk:hydrated', () => {
    document.removeEventListener('click', rememberClick, true);
    if (queuedClick?.isConnected) queuedClick.click();
  }, { once: true });
  if (typeof PerformanceObserver !== 'undefined' && PerformanceObserver.supportedEntryTypes?.includes('paint')) {
    observer = new PerformanceObserver(list => {
      if (list.getEntries().some(entry => entry.name === 'first-contentful-paint')) {
        observer?.disconnect();
        const image = document.querySelector<HTMLImageElement>('[data-testid="section-hero"] img');
        void (image?.decode().catch(() => {}) ?? Promise.resolve()).then(() => {
          requestAnimationFrame(() => requestAnimationFrame(hydrate));
        });
      }
    });
    observer.observe({ type: 'paint', buffered: true });
  } else {
    requestAnimationFrame(() => requestAnimationFrame(hydrate));
  }
} else {
  void start();
}
